/*
 * Stratus chrome: Shop menu preview, and a second measure of the header links. Loaded with defer by snippets/st-header-assets.liquid.
 *
 * snippets/st-menu-preview.liquid renders one [data-st-preview-item] per third-level link of a mega menu, in the
 * order the dropdown lists its .navlink--grandchild links. Hovering or focusing a link shows the item at the same
 * index (an item marked data-st-preview-empty has no photo, so the current one stays) and marks the link
 * .st-is-previewed. Each time the dropdown (hover-disclosure) opens, the first item with a photo comes back.
 * Events are delegated from the document, so a re-rendered header (theme editor) needs no re-binding.
 *
 * Product tiles: a colour swatch is a link to the product page with that colour's variant selected
 * (snippets/product-grid-item.liquid prints `product.url?variant=…`). Palo Alto cancels that click for mouse and touch
 * and only swaps the tile's photo; a capture listener on the document stops the click before Palo Alto's own listener
 * sees it, so the link opens. Hover still swaps the photo.
 */
(() => {
  if (window.StratusChrome && window.StratusChrome.initMenuPreview) return;

  const PREVIEW = '[data-st-preview]';
  const ITEM = '[data-st-preview-item]';
  const LINK = '.navlink--grandchild';
  const ACTIVE = 'is-active';
  const PREVIEWED = 'st-is-previewed';
  const observed = new WeakSet();

  const dropdownOf = (el) => el.closest('hover-disclosure, [data-nav-item]');

  function show(menu, index) {
    const preview = menu.querySelector(PREVIEW);
    if (!preview) return;
    const items = preview.querySelectorAll(ITEM);
    const links = menu.querySelectorAll(LINK);
    const item = items[index];
    if (!item) return;
    links.forEach((link, i) => link.classList.toggle(PREVIEWED, i === index));
    if (item.hasAttribute('data-st-preview-empty')) return;
    items.forEach((el) => el.classList.toggle(ACTIVE, el === item));
  }

  function reset(menu) {
    const items = [...menu.querySelectorAll(`${PREVIEW} ${ITEM}`)];
    const first = items.findIndex((el) => !el.hasAttribute('data-st-preview-empty'));
    if (first < 0) return;
    show(menu, first);
    menu.querySelectorAll(LINK).forEach((link) => link.classList.remove(PREVIEWED));
    menu.querySelectorAll(LINK)[first]?.classList.add(PREVIEWED);
  }

  function onPointer(event) {
    const link = event.target.closest && event.target.closest(LINK);
    if (!link) return;
    const menu = dropdownOf(link);
    if (!menu || !menu.querySelector(PREVIEW)) return;
    show(menu, [...menu.querySelectorAll(LINK)].indexOf(link));
  }

  // Back to the first item whenever the dropdown opens (Palo Alto adds .is-visible to the hover-disclosure).
  function watch(root) {
    root.querySelectorAll(PREVIEW).forEach((preview) => {
      const menu = dropdownOf(preview);
      if (!menu || observed.has(menu)) return;
      observed.add(menu);
      let open = menu.classList.contains('is-visible');
      new MutationObserver(() => {
        const now = menu.classList.contains('is-visible');
        if (now && !open) reset(menu);
        open = now;
      }).observe(menu, { attributes: true, attributeFilter: ['class'] });
      reset(menu);
    });
  }

  document.addEventListener('mouseover', onPointer);
  document.addEventListener('focusin', onPointer);
  document.addEventListener('shopify:section:load', (event) => watch(event.target));

  // Palo Alto's scroll logic reads window.stickyHeaderHeight (60 in head.liquid); keep it equal to the bar
  // st-chrome.css draws (--header-sticky-height), so the dropdown background and hide-on-scroll line up.
  function syncStickyHeight() {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;visibility:hidden;height:var(--header-sticky-height)';
    document.body.appendChild(probe);
    const value = probe.offsetHeight;
    probe.remove();
    if (value > 0) window.stickyHeaderHeight = value;
  }
  if (document.body) syncStickyHeight();
  else document.addEventListener('DOMContentLoaded', syncStickyHeight);
  document.addEventListener('theme:resize:width', syncStickyHeight);

  // Palo Alto measures the bar's links once, when header-component connects, and switches to the burger layout
  // (.site-header--compress) when the wrapper gets narrower than that. The Stratus nav type comes in with a web
  // font, so the first measure can be too short and the links then run into the logo (seen at 768). Measure again
  // once the fonts are in and on load. The clone getMinWidth() takes copies the classes, so the compressed state
  // is lifted for that one synchronous call (no paint in between).
  function remeasureHeader() {
    const header = document.querySelector('header-component');
    if (!header || typeof header.getMinWidth !== 'function' || typeof header.checkWidth !== 'function') return;
    const compressed = header.classList.contains('site-header--compress');
    if (compressed) header.classList.remove('site-header--compress');
    const width = header.getMinWidth();
    if (compressed) header.classList.add('site-header--compress');
    if (width > 0) window.minWidth = width;
    header.checkWidth();
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasureHeader);
  window.addEventListener('load', remeasureHeader);
  document.addEventListener('shopify:section:load', remeasureHeader);

  // Escape closes the Shop menu and the search popdown (Palo Alto), but the focused link inside goes hidden and
  // focus falls back to <body>. Put it back on the control that opened them: the menu's expand button, the search
  // toggle, or the Menu button when the popdown was opened from the mobile menu. Palo Alto closes the search popdown
  // on keyup, and a real key is held longer than the 120 ms check after keydown, so both are watched: whichever
  // finds the control closed puts focus back (the second finds focus already returned and leaves it).
  const returnFocus = (event) => {
    if (event.key !== 'Escape' || !event.target.closest) return;
    const menu = event.target.closest('hover-disclosure');
    const search = menu ? null : event.target.closest('search-popdown');
    if (!menu && !search) return;
    setTimeout(() => {
      const container = menu || search;
      const details = search && search.querySelector('details');
      const closed = menu ? !menu.classList.contains('is-visible') : !(details && details.classList.contains('is-open'));
      if (!closed) return;
      let target = menu
        ? menu.querySelector('.navlink--expand-trigger') || menu.querySelector('.navlink--toplevel')
        : search.querySelector('summary');
      const active = document.activeElement;
      // Leave focus alone if it already moved somewhere real; take it back from <body> or a now-hidden control.
      if (active && active !== document.body && !(container.contains(active) && active !== target)) return;
      if (target && !target.getClientRects().length) target = document.querySelector('[data-mobile-menu-trigger]');
      if (target) target.focus();
    }, 120);
  };
  document.addEventListener('keydown', returnFocus, true);
  document.addEventListener('keyup', returnFocus, true);

  window.StratusChrome = Object.assign(window.StratusChrome || {}, { initMenuPreview: watch, remeasureHeader });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => watch(document));
  } else {
    watch(document);
  }
})();

(() => {
  if (window.StratusSwatchLinks) return;
  window.StratusSwatchLinks = true;
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest && event.target.closest('.product-grid-item .swatch__link[data-swatch-variant]');
      if (link) event.stopPropagation(); // capture phase: Palo Alto's preventDefault never runs, the link opens
    },
    true
  );
})();
